import { Button, Chip, Typography } from '@mui/material';
import { FlexLayout, GridLayout } from '@Styled/utilities';
import moment from 'moment';
import React, { FC } from 'react';
import styled from 'styled-components';
import { AssignmentSubmission, productOf } from './Submissions.types';

type Props = {
	submission: AssignmentSubmission | undefined;
	onClick: (submission: AssignmentSubmission) => void;
	/** Show the classroom / standalone-course badge (global tab only). */
	showProduct?: boolean;
	/** Button label: "Score" for graders, "View" otherwise. */
	actionLabel?: string;
};

const AssignmentSubmissionCard: FC<Props> = ({ submission, onClick, showProduct = false, actionLabel = 'View' }) => {
	const product = productOf(submission?.assignment.unit.course);
	return (
		<Card>
			<GridWrapper gridTemplateColumns="1.3fr 1fr 1fr 0.5fr">
				<FlexLayout
					flexDirection="column"
					alignItems="flex-start"
					gridGap="1.875rem"
				>
					<BoldText>{submission?.user.username}</BoldText>
					<GrayText>
						{submission?.user.first_name} {submission?.user.last_name}
					</GrayText>
				</FlexLayout>
				<FlexLayout
					flexDirection="column"
					alignItems="flex-start"
					gridGap="1.875rem"
				>
					{showProduct && product.kind !== 'unknown' && (
						<Chip
							size="small"
							label={`${product.kind === 'classroom' ? 'Classroom' : 'Standalone'}${product.title ? ` · ${product.title}` : ''}`}
							color={product.kind === 'classroom' ? 'primary' : 'secondary'}
							variant="outlined"
						/>
					)}
					<BoldText>{submission?.assignment.unit.course.name}</BoldText>
					<GrayText>{submission?.assignment.unit.name}</GrayText>
				</FlexLayout>
				<FlexLayout
					flexDirection="column"
					alignItems="flex-start"
					gridGap="1.875rem"
				>
					<GrayText>
						{moment(submission?.updated_at).format('DD/MM/Y hh:mm A')}
					</GrayText>
					{submission?.score != null ? (
						<FlexLayout gridGap={'0.5rem'}>
							<Typography>
								Score {submission.score}/{submission.assignment.overall_score}
							</Typography>
							<Typography
								color={submission.status === 'passed' ? 'green' : 'red'}
							>
								{submission.status}
							</Typography>
						</FlexLayout>
					) : (
						<Chip size="small" label="Pending" sx={{ backgroundColor: '#ffab00', color: '#fff' }} />
					)}
				</FlexLayout>
				<FlexLayout flexDirection="column" alignItems="flex-end">
					<ViewButton
						onClick={() => submission && onClick(submission)}
						variant="outlined"
						color="primary"
					>
						{actionLabel}
					</ViewButton>
				</FlexLayout>
			</GridWrapper>
		</Card>
	);
};

const Card = styled.div`
	box-shadow: 0px 3px 6px #00000029;
	border: 1px solid #f5f5f5;
	border-radius: 5px;
	padding: 1.188rem 2rem;
	margin-bottom: 1rem;
`;
const GridWrapper = styled(GridLayout)`
	align-items: center;
`;
const ViewButton = styled(Button)`
	min-width: 3.5rem;
	border-radius: 4px;
	font-size: 0.813rem;
	font-weight: 600;
`;

const BoldText = styled.p`
	font-size: 1.125rem;
	font-weight: 600;
	color: #251b33;
`;
const GrayText = styled.p`
	font-size: 1.125rem;
	font-weight: 500;
	color: #a4a4a4;
`;

export default AssignmentSubmissionCard;
